import match from "autosuggest-highlight/match";
import { isNil } from "lodash";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  Api,
  API_BASE,
  companyIdSelector,
  createErrorResult,
  createSuccessResult,
  errors,
  getAuthorizationHeader,
  ICoordinate,
  IFindAddressByLocation,
  IFindAddressesByInput,
  IFindLocationByAddress,
  IPlacePrediction,
  IWarkDistanceAndTime,
  LANG_DEFAULT,
  Log,
  SECONDS_IN_MINUTE,
  SimpleResult,
  TLatLngExpression,
} from "@ryde/shared";

/**
 * One autocomplete row as the backend now answers it: `@thinwrap/location`'s
 * normalized `IAutocompletePrediction`, off Google's frozen legacy Places API.
 *
 * It carries no match offsets — see `buildMatches`, which derives them here instead.
 */
type BackendPrediction = {
  description: string;
  placeId?: string;
  structuredFormat?: {
    mainText: string;
    secondaryText?: string;
  };
};

interface IBackendGeocodeResult {
  formatted_address: string;
  place_id: string;
  types: string[];
}

interface IBackendGeocodeResponse {
  results: IBackendGeocodeResult[];
}

/**
 * Code-point index -> UTF-16 index, for every position in `text` including the end.
 *
 * `match` counts in code points while `parse` slices with `String.prototype.slice`, which
 * counts UTF-16 units. The two agree until a character outside the BMP appears — one
 * emoji in a POI name shifts every later highlight by one and the bold range lands on the
 * wrong characters. Hebrew and Latin are unaffected, which is why this only shows up on
 * names like "Cafe 🚀 Rocket".
 */
const buildUtf16OffsetTable = (text: string): number[] => {
  const offsets: number[] = [];
  let utf16Index = 0;

  for (const char of text) {
    offsets.push(utf16Index);
    utf16Index += char.length;
  }
  offsets.push(utf16Index);

  return offsets;
};

/**
 * The ranges of `text` that matched what the user typed, as `{ startOffset, endOffset }` pairs — this
 * is what makes the matched part of a suggestion render bold.
 *
 * Derived from the query rather than read off the provider. Every geocoder spells its
 * offsets differently (and the normalized backend response drops them), whereas the
 * query is the same everywhere — so this keeps highlighting working whichever provider
 * answers, and it cannot disagree with what is actually in the input box.
 *
 * `match` handles diacritics and matches at word starts, and returns ranges sorted and
 * non-overlapping, which is what `parse` expects.
 */
const buildMatches = (
  text: string,
  query: string,
): { startOffset: number; endOffset: number }[] => {
  const ranges = match(text, query);
  if (!ranges.length) {
    return [];
  }

  const codePointCount = Array.from(text).length;
  if (codePointCount === text.length) {
    return ranges.map(([startOffset, endOffset]) => ({
      startOffset,
      endOffset,
    }));
  }

  const utf16Offsets = buildUtf16OffsetTable(text);

  return ranges.map(([start, end]) => ({
    startOffset: utf16Offsets[start] ?? start,
    endOffset: utf16Offsets[end] ?? end,
  }));
};

/** Build a single prediction from what the backend returned. */
const buildPredictionLayout = (
  p: BackendPrediction,
  query: string,
): IPlacePrediction => {
  return {
    placeId: p.placeId,
    text: {
      text: p.description,
      matches: buildMatches(p.description, query),
    },
    mainText: p.structuredFormat
      ? {
          text: p.structuredFormat.mainText,
          matches: buildMatches(p.structuredFormat.mainText, query),
        }
      : null,
    secondaryText: p.structuredFormat?.secondaryText
      ? {
          text: p.structuredFormat.secondaryText,
          matches: [],
        }
      : null,
  };
};

/** Map backend autocomplete predictions onto the app's own prediction shape. */
const mapPredictionsToPlacePredictions = (
  predictions: BackendPrediction[],
  query: string,
): IPlacePrediction[] =>
  (predictions || [])
    .filter(p => !isNil(p?.placeId))
    .map(p => buildPredictionLayout(p, query));

export const useGoogleServices = (
  apiBase: string = API_BASE,
  authorize = false,
) => {
  /*
   * Address autocomplete answers from the company's own `addressSearchBounds` /
   * `addressSearchRegionCodes`, and the backend cannot infer which company that is: a super
   * admin's user row carries no company, so the tenant currently being browsed has to travel
   * in the path, as it does on every other company-scoped call. A landing page has no profile
   * and so no company, and falls back to the unscoped route and the default area.
   */
  const companyId = useRecoilValue(companyIdSelector);
  const autocompleteUrl = companyId
    ? `${apiBase}/c/${companyId}/google/autocomplete`
    : `${apiBase}/google/autocomplete`;
  const getCoordinateByPlaceId = useEvent(
    async (placeId?: string): Promise<SimpleResult<ICoordinate>> => {
      if (!placeId) {
        return createErrorResult(
          errors.map.getCoordinateByPlaceId,
          "Missing placeId",
        );
      }
      try {
        const response = await Api.get<{
          results: {
            geometry: { location: ICoordinate };
          }[];
        }>(
          `${apiBase}/google/geocode/place-id`,
          { placeId },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const data = response?.data;
        if (!data?.results?.length) {
          return createErrorResult(
            errors.map.getCoordinateByPlaceId,
            "Invalid coordinates returned from Google API",
          );
        }
        const loc = data.results[0].geometry.location;
        return createSuccessResult({ lat: loc.lat, lng: loc.lng });
      } catch (err) {
        Log.warn("Exception while getting coordinates from Google API", err);
        return createErrorResult(
          errors.map.getCoordinateByPlaceId,
          "Exception while getting coordinates from Google API",
        );
      }
    },
  );

  const searchPlacesByInput = useEvent(
    async ({
      inputParams,
    }: IFindAddressesByInput): Promise<SimpleResult<IPlacePrediction[]>> => {
      // Google bills an autocomplete request even when the query cannot return a
      // completion, and the Autocomplete components mount blank and clear back to blank.
      if (!inputParams.input?.trim()) {
        return createSuccessResult([]);
      }

      try {
        const response = await Api.get<
          | { status: string; message: string; data: BackendPrediction[] }
          | BackendPrediction[]
        >(
          autocompleteUrl,
          {
            input: inputParams.input,
            languageCode: inputParams.language ?? LANG_DEFAULT,
            ...(inputParams.sessionToken
              ? { sessionToken: inputParams.sessionToken }
              : {}),
          },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const raw = response?.data;
        const predictions = Array.isArray(raw)
          ? (raw as BackendPrediction[])
          : (raw as { data?: BackendPrediction[] })?.data;
        if (!Array.isArray(predictions)) {
          return createErrorResult(
            errors.map.searchPlacesByInput,
            "Invalid predictions returned from Google API",
          );
        }
        const result = mapPredictionsToPlacePredictions(
          predictions,
          inputParams.input,
        );
        return createSuccessResult(result);
      } catch (err) {
        Log.warn("Exception while getting predictions from Google API", err);
        return createErrorResult(
          errors.map.searchPlacesByInput,
          "Exception while getting predictions from Google API",
        );
      }
    },
  );

  const searchAddressByGeo = useEvent(
    async ({
      geoParams,
    }: IFindAddressByLocation): Promise<SimpleResult<IPlacePrediction>> => {
      try {
        const { location, language } = geoParams;
        const geoResponse = await Api.get<IBackendGeocodeResponse>(
          `${apiBase}/google/geocode/latlng`,
          {
            lat: location.lat,
            lng: location.lng,
            languageCode: language ?? LANG_DEFAULT,
          },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const geoData = geoResponse?.data;
        if (!geoData?.results?.length) {
          return createErrorResult(
            errors.map.searchAddressByGeo,
            "Invalid coordinates from geo returned from Google API",
          );
        }
        const bestResult =
          geoData.results.find(r => !r.types?.includes("plus_code")) ??
          geoData.results[0];
        const { formatted_address: formattedAddress, place_id: placeId } =
          bestResult;

        return createSuccessResult(
          // A reverse geocode has no typed query, so nothing is highlighted.
          buildPredictionLayout({ description: formattedAddress, placeId }, ""),
        );
      } catch (err) {
        Log.warn("Exception while getting predictions from Google API", err);
        return createErrorResult(
          errors.map.searchAddressByGeo,
          "Exception while getting predictions from Google API",
        );
      }
    },
  );

  const searchLocationByAddress = useEvent(
    async ({
      address,
    }: IFindLocationByAddress): Promise<SimpleResult<ICoordinate>> => {
      try {
        const response = await Api.get<{
          results: {
            geometry: { location: ICoordinate };
          }[];
        }>(
          `${apiBase}/google/geocode/address`,
          { address },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const data = response?.data;
        if (!data?.results?.length) {
          return createErrorResult(
            errors.map.searchAddressByGeo,
            "No location found for the given address",
          );
        }
        const loc = data.results[0].geometry.location;
        return createSuccessResult({ lat: loc.lat, lng: loc.lng });
      } catch (err) {
        Log.warn("Exception while getting location from Google API", err);
        return createErrorResult(
          errors.map.searchAddressByGeo,
          "Exception while getting location from Google API",
        );
      }
    },
  );

  const getWalkingDistanceAndTime = useEvent(
    async ({
      origin,
      destination,
    }: {
      origin: ICoordinate;
      destination: ICoordinate;
    }): Promise<SimpleResult<IWarkDistanceAndTime>> => {
      try {
        const response = await Api.post<{
          routes: {
            legs: {
              distance: { value: number };
              duration: { value: number };
            }[];
            overview_path: ICoordinate[];
          }[];
        }>(
          `${apiBase}/google/directions/walking`,
          { origin, destination },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const data = response?.data;
        if (!data?.routes?.length) {
          return createErrorResult(
            errors.map.getWalkingDistanceAndTime,
            "No walking route found between the given points",
          );
        }
        const route = data.routes[0];
        const path: TLatLngExpression[] = (route?.overview_path ?? []).map(
          p => [p.lat, p.lng],
        );
        const leg = route.legs?.[0];
        const distanceInMeters = leg?.distance?.value ?? 0;
        const durationInSeconds = leg?.duration?.value ?? 0;
        const minutes = Math.floor(durationInSeconds / SECONDS_IN_MINUTE);
        const seconds = durationInSeconds % SECONDS_IN_MINUTE;
        return createSuccessResult({
          distance: distanceInMeters,
          duration: { minutes, seconds },
          path,
        });
      } catch (err) {
        Log.warn("Exception while calculating walking distance and time", err);
        return createErrorResult(
          errors.map.getWalkingDistanceAndTime,
          "Exception while calculating walking distance and time",
        );
      }
    },
  );

  const getPlaceDetailsByPlaceId = useEvent(
    async ({
      placeId,
      sessionToken,
      languageCode,
    }: {
      placeId?: string;
      sessionToken?: string | null;
      languageCode?: string;
    }): Promise<SimpleResult<ICoordinate>> => {
      if (!placeId) {
        return createErrorResult(
          errors.map.getCoordinateByPlaceId,
          "Missing placeId",
        );
      }
      try {
        // A normalized `IGeocodeCandidate`, not the legacy `{ result: { geometry } }`.
        const response = await Api.get<{ location: ICoordinate }>(
          `${apiBase}/google/place-details`,
          {
            placeId,
            languageCode: languageCode ?? LANG_DEFAULT,
            ...(sessionToken ? { sessionToken } : {}),
          },
          undefined,
          authorize ? getAuthorizationHeader() : undefined,
        );
        const data = response?.data;
        if (!data?.location) {
          return createErrorResult(
            errors.map.getCoordinateByPlaceId,
            "Invalid coordinates returned from place details API",
          );
        }
        const loc = data.location;
        return createSuccessResult({ lat: loc.lat, lng: loc.lng });
      } catch (err) {
        Log.warn("Exception while getting place details from Google API", err);
        return createErrorResult(
          errors.map.getCoordinateByPlaceId,
          "Exception while getting place details from Google API",
        );
      }
    },
  );

  return {
    getCoordinateByPlaceId,
    getPlaceDetailsByPlaceId,
    searchAddressByGeo,
    searchPlacesByInput,
    searchLocationByAddress,
    getWalkingDistanceAndTime,
  };
};
